import React, { useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { categories, gradeLevels } from '../../constants/categories';

const DEBOUNCE_DELAY = 500;

const formatPreviewLabel = (items, formatter = (v) => v) => {
  if (!items || items.length === 0) return '';
  const displayItems = items.slice(0, 2).map(formatter);
  const hiddenCount = items.length - 2;
  return hiddenCount > 0
    ? `${displayItems.join(', ')}, +${hiddenCount}`
    : displayItems.join(', ');
};

const SearchFilterBar = ({
  searchTerm,
  viewType,
  selectedGrades = [],
  selectedCategories = [],
  readingCategories = [],
  onSearchChange,
  onSearchSubmit,
  onViewTypeChange,
  onGradesChange,
  onCategoriesChange,
}) => {
  const debounceTimeout = useRef(null);
  const latestSearch = useRef(searchTerm);
  const latestGrades = useRef(selectedGrades);
  const latestCategories = useRef(selectedCategories);

  useEffect(() => {
    // Clear any existing timeout
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    
    // Set a new timeout to trigger search after typing stops
    debounceTimeout.current = setTimeout(() => {
      if (searchTerm !== latestSearch.current || 
          !areArraysEqual(selectedCategories, latestCategories.current) || 
          !areArraysEqual(selectedGrades, latestGrades.current)) {
        
        latestSearch.current = searchTerm;
        latestCategories.current = selectedCategories;
        latestGrades.current = selectedGrades;
        onSearchSubmit(searchTerm, selectedCategories, selectedGrades);
      }
    }, DEBOUNCE_DELAY);
    
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
    // Include all dependencies that should trigger the effect
  }, [searchTerm, selectedCategories, selectedGrades, onSearchSubmit]);

  // Helper function to properly compare arrays
  const areArraysEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((val, index) => val === arr2[index]);
  };

  const handleSearchChange = (event) => onSearchChange(event.target.value);
  
  const handleGradeLevelChange = (event) => {
    onGradesChange(event.target.value);
  };
  
  const handleCategoriesChange = (event) => {
    onCategoriesChange(event.target.value);
  };

  const handleClearSearch = () => {
    onSearchChange('');
    onSearchSubmit('');
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSearchSubmit(searchTerm, selectedCategories, selectedGrades);
    }
  };

  const handleClearFilters = () => {
    onSearchChange('');
    onGradesChange([]);
    onCategoriesChange([]);
    onSearchSubmit('', [], []);
  };

  const activeFilterCount =
    selectedGrades.length + selectedCategories.length + (searchTerm ? 1 : 0);

  return (
    <Box
      sx={{
        px: 3,
        py: 3,
        borderRadius: 2,
        backgroundColor: (theme) => theme.palette.background.paper,
        boxShadow: '0px 2px 8px rgba(0,0,0,0.2)',
        marginBottom: 2,
      }}
    >
      {/* Improved layout with fixed widths */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '3fr 1fr 1fr 80px',
          gap: 2,
          alignItems: 'center',
          width: '100%',
        }}
      >
        {/* Search input */}
        <TextField
          variant="outlined"
          placeholder="Search by title"
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyPress}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Grade Filter */}
        <FormControl size="small" fullWidth>
          <InputLabel>Grade Level</InputLabel>
          <Tooltip
            title={selectedGrades.map((v) => `Grade Level ${v}`).join(', ')}
            arrow
            disableHoverListener={selectedGrades.length === 0}
          >
            <Select
              multiple
              value={selectedGrades}
              onChange={handleGradeLevelChange}
              input={<OutlinedInput label="Grade Level" />}
              renderValue={(selected) => (
                <Typography noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {formatPreviewLabel(selected, (v) => `Grade Level ${v}`)}
                </Typography>
              )}
            >
              {gradeLevels.map((grade) => (
                <MenuItem key={grade.value} value={grade.value}>
                  {grade.label}
                </MenuItem>
              ))}
            </Select>
          </Tooltip>
        </FormControl>

        {/* Category Filter */}
        <FormControl size="small" fullWidth>
          <InputLabel>Categories</InputLabel>
          <Tooltip
            title={selectedCategories.map((val) => {
              const cat = readingCategories?.find(c => c.id === val);
              return cat ? cat.name : categories[val] || 'Unknown';
            }).join(', ')}
            arrow
            disableHoverListener={selectedCategories.length === 0}
          >
            <Select
              multiple
              value={selectedCategories}
              onChange={handleCategoriesChange}
              input={<OutlinedInput label="Categories" />}
              renderValue={(selected) => (
                <Typography noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {formatPreviewLabel(selected, (val) => {
                    const cat = readingCategories?.find(c => c.id === val);
                    return cat ? cat.name : categories[val] || val;
                  })}
                </Typography>
              )}
            >
              {(readingCategories && readingCategories.length > 0 ? readingCategories : categories.map((name, id) => ({ id, name }))).map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </Tooltip>
        </FormControl>

        {/* Clear Button */}
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClearFilters}
          disabled={activeFilterCount === 0}
          startIcon={<ClearIcon />}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            height: '40px',
            width: '80px',
          }}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
};

export default SearchFilterBar;